import { useEffect, useState, useCallback } from 'react';
import {
  UserPlus, Pencil, Trash2, RefreshCw, Shield, CheckCircle2,
  XCircle, Key, Search, ChevronDown, ChevronUp, X,
} from 'lucide-react';
import {
  getInternalTeam, createInternalMember, updateInternalMember,
  deleteInternalMember, resetMemberPassword,
} from '@/services/apiClient';
import { SA_PERMISSION_GROUPS, SA_PERMISSIONS } from '@/constants/saPermissions';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusBadge({ active }) {
  return active ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
      <CheckCircle2 className="h-3 w-3" /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-600 dark:text-red-400">
      <XCircle className="h-3 w-3" /> Inactive
    </span>
  );
}

// ── Permission Picker ─────────────────────────────────────────────────────────
function PermissionPicker({ selected, onChange }) {
  const toggle = (key) => {
    onChange(selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key]);
  };
  const toggleAll = () => {
    onChange(selected.length === SA_PERMISSIONS.length ? [] : [...SA_PERMISSIONS]);
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">SA Panel Access</span>
        <button
          type="button"
          onClick={toggleAll}
          className="text-[11px] text-primary hover:underline font-medium"
        >
          {selected.length === SA_PERMISSIONS.length ? 'Deselect all' : 'Select all'}
        </button>
      </div>
      {SA_PERMISSION_GROUPS.map((group) => (
        <div key={group.id}>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{group.label}</p>
          <div className="space-y-1.5">
            {group.permissions.map((perm) => {
              const checked = selected.includes(perm.key);
              return (
                <label
                  key={perm.key}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                    checked ? 'border-primary/40 bg-primary/5' : 'border-border/50 hover:bg-muted/40'
                  )}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                    checked={checked}
                    onChange={() => toggle(perm.key)}
                  />
                  <div>
                    <p className="text-xs font-semibold text-foreground">{perm.label}</p>
                    <p className="text-[11px] text-muted-foreground">{perm.description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

// ── Member Form ───────────────────────────────────────────────────────────────
function MemberForm({ initial, onSubmit, onClose, loading, submitLabel }) {
  const [form, setForm] = useState({
    firstName:     initial?.firstName || '',
    lastName:      initial?.lastName  || '',
    username:      initial?.email?.replace('@echohire.in', '') || '',
    saPermissions: initial?.saPermissions || [],
    isActive:      initial?.isActive !== undefined ? initial.isActive : true,
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.firstName.trim() || !form.lastName.trim() || (!initial && !form.username.trim())) {
      setError('First name, last name, and username are required.');
      return;
    }
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err.message || 'An error occurred.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-lg bg-red-500/10 border border-red-400/30 p-3 text-xs text-red-600 dark:text-red-400">{error}</div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">First Name *</label>
          <input
            type="text"
            value={form.firstName}
            onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            placeholder="Raj"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Last Name *</label>
          <input
            type="text"
            value={form.lastName}
            onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
            placeholder="Sharma"
          />
        </div>
      </div>

      {/* Username — only editable on create */}
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          {initial ? 'Email' : 'Username *'}
        </label>
        {initial ? (
          <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">{initial.email}</p>
        ) : (
          <div className="flex items-center rounded-lg border border-border bg-background overflow-hidden focus-within:ring-2 focus-within:ring-primary/40">
            <input
              type="text"
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value.replace(/\s/g, '').toLowerCase() }))}
              className="flex-1 bg-transparent px-3 py-2 text-sm text-foreground focus:outline-none"
              placeholder="raj.sharma"
            />
            <span className="shrink-0 border-l border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">@echohire.in</span>
          </div>
        )}
        {!initial && (
          <p className="mt-1 text-[11px] text-muted-foreground">Email will be: {form.username || 'username'}@echohire.in</p>
        )}
      </div>

      {/* Active toggle — only on edit */}
      {initial && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/20 px-4 py-3">
          <div>
            <p className="text-xs font-medium text-foreground">Account Active</p>
            <p className="text-[11px] text-muted-foreground">Inactive accounts cannot log in</p>
          </div>
          <button
            type="button"
            onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
            className={cn(
              'relative h-6 w-11 rounded-full transition-colors',
              form.isActive ? 'bg-emerald-500' : 'bg-muted'
            )}
          >
            <span className={cn(
              'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
              form.isActive ? 'translate-x-5' : 'translate-x-0.5'
            )} />
          </button>
        </div>
      )}

      {/* Default password note — only on create */}
      {!initial && (
        <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 px-4 py-3">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">Default Password</p>
          <p className="text-[11px] text-amber-600/80 dark:text-amber-400/80 mt-0.5">
            Account will be created with password <code className="font-mono font-bold">12345678</code>. A welcome email will be sent.
          </p>
        </div>
      )}

      {/* Permissions */}
      <div className="border-t border-border pt-4">
        <PermissionPicker
          selected={form.saPermissions}
          onChange={perms => setForm(f => ({ ...f, saPermissions: perms }))}
        />
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={loading} className="flex-1">
          {loading ? <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
          {submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SuperAdminTeamPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch]   = useState('');
  const [modal, setModal]     = useState(null); // 'create' | { edit: member } | { del: member } | { reset: member }
  const [expandedPerms, setExpandedPerms] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInternalTeam();
      setMembers(res.data || []);
    } catch { /* toast handled by apiClient */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = members.filter(m =>
    `${m.firstName} ${m.lastName} ${m.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async (form) => {
    setSubmitting(true);
    try {
      await createInternalMember({ firstName: form.firstName, lastName: form.lastName, username: form.username, saPermissions: form.saPermissions });
      setModal(null);
      load();
    } finally { setSubmitting(false); }
  };

  const handleEdit = async (form) => {
    setSubmitting(true);
    try {
      await updateInternalMember(modal.edit._id, { firstName: form.firstName, lastName: form.lastName, saPermissions: form.saPermissions, isActive: form.isActive });
      setModal(null);
      load();
    } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await deleteInternalMember(modal.del._id);
      setModal(null);
      load();
    } finally { setSubmitting(false); }
  };

  const handleReset = async () => {
    setSubmitting(true);
    try {
      await resetMemberPassword(modal.reset._id);
      setModal(null);
    } finally { setSubmitting(false); }
  };

  const togglePerms = (id) => setExpandedPerms(p => ({ ...p, [id]: !p[id] }));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Internal Team</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Manage EchoHire staff access to the Super Admin panel</p>
        </div>
        <Button onClick={() => setModal('create')} size="sm" className="gap-2">
          <UserPlus className="h-4 w-4" /> Add Member
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search members…"
          className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
            <Shield className="h-10 w-10 opacity-30" />
            <p className="text-sm">{search ? 'No members match your search' : 'No internal team members yet'}</p>
            {!search && (
              <Button size="sm" variant="outline" onClick={() => setModal('create')} className="gap-2">
                <UserPlus className="h-3.5 w-3.5" /> Add First Member
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  {['Member', 'Email', 'Status', 'SA Access', 'Added', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const permLabels = SA_PERMISSION_GROUPS.flatMap(g => g.permissions)
                    .filter(p => m.saPermissions?.includes(p.key))
                    .map(p => p.label);
                  const showAll = expandedPerms[m._id];
                  const visible = showAll ? permLabels : permLabels.slice(0, 3);
                  return (
                    <tr key={m._id} className="border-b border-border/50 last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-xs font-bold text-primary-foreground">
                            {m.firstName?.[0]?.toUpperCase()}{m.lastName?.[0]?.toUpperCase()}
                          </div>
                          <span className="font-medium text-foreground">{m.firstName} {m.lastName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{m.email}</td>
                      <td className="px-4 py-3"><StatusBadge active={m.isActive} /></td>
                      <td className="px-4 py-3">
                        {permLabels.length === 0 ? (
                          <span className="text-xs text-muted-foreground italic">No access</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {visible.map(label => (
                              <span key={label} className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                                {label}
                              </span>
                            ))}
                            {permLabels.length > 3 && (
                              <button
                                type="button"
                                onClick={() => togglePerms(m._id)}
                                className="flex items-center gap-0.5 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:text-foreground"
                              >
                                {showAll ? (
                                  <><ChevronUp className="h-2.5 w-2.5" /> less</>
                                ) : (
                                  <><ChevronDown className="h-2.5 w-2.5" /> +{permLabels.length - 3} more</>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(m.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setModal({ edit: m })}
                            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setModal({ reset: m })}
                            className="rounded-md p-1.5 text-muted-foreground hover:bg-amber-500/20 hover:text-amber-600 transition-colors"
                            title="Reset password"
                          >
                            <Key className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setModal({ del: m })}
                            className="rounded-md p-1.5 text-muted-foreground hover:bg-red-500/10 hover:text-red-500 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info card */}
      <div className="rounded-xl border border-border bg-card/50 p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-4 w-4 shrink-0 text-primary mt-0.5" />
          <div className="text-xs text-muted-foreground space-y-1">
            <p className="font-semibold text-foreground">How internal team access works</p>
            <p>Members use their <code className="font-mono">@echohire.in</code> email to log in at <strong>app.echohire.in</strong>. They see only the Super Admin sections you grant them — nothing else is visible in their sidebar.</p>
            <p>The true Super Admin account always has full access. Only you can manage team members.</p>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {modal === 'create' && (
        <Modal title="Add Internal Team Member" onClose={() => setModal(null)}>
          <MemberForm onSubmit={handleCreate} onClose={() => setModal(null)} loading={submitting} submitLabel="Create Member" />
        </Modal>
      )}

      {modal?.edit && (
        <Modal title="Edit Member" onClose={() => setModal(null)}>
          <MemberForm initial={modal.edit} onSubmit={handleEdit} onClose={() => setModal(null)} loading={submitting} submitLabel="Save Changes" />
        </Modal>
      )}

      {modal?.del && (
        <Modal title="Delete Member" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete <strong className="text-foreground">{modal.del.firstName} {modal.del.lastName}</strong> ({modal.del.email})?
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="destructive" onClick={handleDelete} disabled={submitting} className="flex-1">
                {submitting ? <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Trash2 className="mr-2 h-3.5 w-3.5" />}
                Delete Member
              </Button>
              <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            </div>
          </div>
        </Modal>
      )}

      {modal?.reset && (
        <Modal title="Reset Password" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will reset <strong className="text-foreground">{modal.reset.firstName} {modal.reset.lastName}</strong>'s password back to the default <code className="font-mono font-bold">12345678</code>.
            </p>
            <div className="flex gap-3">
              <Button onClick={handleReset} disabled={submitting} className="flex-1">
                {submitting ? <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Key className="mr-2 h-3.5 w-3.5" />}
                Reset to Default
              </Button>
              <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
